import { deepAnalysisApi } from '../service';
import {
    WebsiteIndexEntry,
    FetchWebsiteIndexEntryParams,
    AddWebsiteIndexEntryParams,
    UpdateWebsiteIndexEntryParams,
    DeleteWebsiteIndexEntryParams,
    FetchWebsiteIndexStatusResponse,
    FetchWebsiteIndexStatusParams,
    WebsiteIndexScheduledTasks,
    FetchWebsiteIndexScheduledTasksParams,
    OnPageSeoScore,
    FetchOnPageSeoScoreParams,
} from './types';

const websiteIndexEntryApi = deepAnalysisApi.injectEndpoints({
    endpoints: (builder) => ({
        fetchWebsiteIndexEntries: builder.query<WebsiteIndexEntry[], void>({
            query: () => {
                return {
                    method: 'GET',
                    url: '/website-index',
                };
            },

            providesTags: [{ type: 'WebsiteIndexEntries', id: 'LIST' }],
        }),

        fetchWebsiteIndexEntry: builder.query<WebsiteIndexEntry, FetchWebsiteIndexEntryParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'GET',
                    url: `/website-index/${guid}`,
                };
            },

            providesTags: (result, error, args) => [{ type: 'WebsiteIndexEntries', id: args.guid }],
        }),

        fetchWebsiteIndexStatus: builder.query<FetchWebsiteIndexStatusResponse, FetchWebsiteIndexStatusParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'GET',
                    url: `/website-index/setup-status/${guid}`,
                };
            },
        }),

        fetchWebsiteIndexScheduledTasks: builder.query<WebsiteIndexScheduledTasks, FetchWebsiteIndexScheduledTasksParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'GET',
                    url: `/website-index/scheduled-tasks/${guid}`,
                };
            },
        }),

        fetchOnPageSeoScore: builder.query<OnPageSeoScore, FetchOnPageSeoScoreParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'GET',
                    url: `/website-index/on-page-seo-score/${guid}`,
                };
            },
        }),

        addWebsiteIndexEntry: builder.mutation<WebsiteIndexEntry, AddWebsiteIndexEntryParams>({
            query: (args) => {
                return {
                    method: 'POST',
                    body: args,
                    url: `/website-index`,
                };
            },

            invalidatesTags: [{ type: 'WebsiteIndexEntries', id: 'LIST' }],
        }),

        updateWebsiteIndexEntry: builder.mutation<WebsiteIndexEntry, UpdateWebsiteIndexEntryParams>({
            query: (args) => {
                const { guid, ...rest } = args;

                return {
                    method: 'PATCH',
                    body: rest,
                    url: `/website-index/${guid}`,
                };
            },

            invalidatesTags: (result, error, args) => [
                { type: 'WebsiteIndexEntries', id: 'LIST' },
                { type: 'WebsiteIndexEntries', id: args.guid },
            ],
        }),

        deleteWebsiteIndexEntry: builder.mutation<void, DeleteWebsiteIndexEntryParams>({
            query: (args) => {
                const { guid } = args;

                return {
                    method: 'DELETE',
                    url: `/website-index/${guid}`,
                };
            },

            invalidatesTags: [{ type: 'WebsiteIndexEntries', id: 'LIST' }],
        }),
    }),
});

export const {
    useAddWebsiteIndexEntryMutation,
    useDeleteWebsiteIndexEntryMutation,
    useFetchWebsiteIndexEntriesQuery,
    useLazyFetchWebsiteIndexEntriesQuery,
    useFetchWebsiteIndexEntryQuery,
    useLazyFetchWebsiteIndexEntryQuery,
    useUpdateWebsiteIndexEntryMutation,
    useFetchWebsiteIndexStatusQuery,
    useFetchWebsiteIndexScheduledTasksQuery,
    useFetchOnPageSeoScoreQuery,
} = websiteIndexEntryApi;
